/**
 * ContentOnImage.tsx — Renders a panel of content overlaid on a background image.
 *
 * JSON trigger: `uiCompType.method: "renderContentOnImage"`
 *
 * **Layout layers (outermost → innermost):**
 * ```
 * <div containerStyles>        ← outer wrapper (position: relative)
 *   <Image fill>               ← background image (position: absolute)
 *   <div contentParentStyles>  ← content overlay (position: absolute)
 *     <Panel>                  ← any Panel content via sectionData.content
 *   </div>
 * </div>
 * ```
 *
 * **Data fields:**
 *  - `sectionData.imageUrl`   — Background image URL (supports hash-encoded styles)
 *  - `sectionData.content`    — Nested `SectionData` rendered by `Panel`
 *
 * **Key options:**
 *  - `options.preload`        — Passes `priority` to Next.js `<Image>` for LCP images
 *
 * **Mobile:** `uiCompType.mStyles` replaces `styles` on small screens.
 *
 * **Tracking:** Calls `trackItem` with `sectionData.trackInfo` on click.
 *
 * **Style slots:** `styles`/`mStyles` (container), content position controlled
 * via `sectionData.content.uiCompType.parentStyles`.
 */
import React, { useEffect, useState } from "react";
import Image from 'next/image';
import cssStyles from "./cms_renderer.module.scss";
import { SectionData, ComponentRenderProps } from "./index";
import Panel from "./Panel";
import { trackItem } from "@/utils";
import { useDeviceType } from "@/hooks/useDeviceType";

interface ContentOnImageProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
}

const ContentOnImage = ({ sectionData, props }: ContentOnImageProps): JSX.Element | null => {
    //TODO: Add support for mobile
    const deviceType = useDeviceType();
    const [styles, setStyles] = useState(sectionData.uiCompType?.styles);
    useEffect(() => {
        if (deviceType === "Mobile" && sectionData.uiCompType?.mStyles) {
            setStyles(sectionData.uiCompType?.mStyles);
        }
    }, [deviceType, sectionData.uiCompType?.mStyles]);

    const panelClassName = "contentOnImage";
    const isPreload = sectionData.uiCompType?.options?.preload || false;

    const renderContentOnImage = () => (
        <div className={cssStyles[panelClassName + "Container"]} style={styles}>
            {sectionData.content && (
                <div className={cssStyles[panelClassName + "Content"]} style={(sectionData.content as SectionData)?.uiCompType?.parentStyles}>
                    {Panel({ sectionData: sectionData.content as SectionData, props })}
                </div>
            )}
            {sectionData.backgroundImageUrl && (
                <Image
                    alt={sectionData.title || sectionData.backgroundImageUrl || ''}
                    src={sectionData.backgroundImageUrl}
                    layout="fill"
                    objectFit="cover"
                    priority={isPreload}
                    loading={isPreload ? "eager" : "lazy"}
                />
            )}
        </div>
    );

    return sectionData.link ? (
        <a
            id={sectionData.trackId || ('link_')}
            href={sectionData.link}
            target={sectionData.newTab ? "_blank" : "_self"}
            onClick={(e) => {
                trackItem(sectionData.trackInfo || { event: 'contentOnImageLinkClick', section: sectionData.trackId });
            }}
        >
            {renderContentOnImage()}
        </a>
    ) : (
        renderContentOnImage()
    );
}
export default ContentOnImage;
